/**
 * JournalTab — Project Journal: a curated SHARE feed (Phase A thin vertical).
 *
 * The Journal is NOT an authoring surface. Authorized people SHARE existing
 * content into it; the project Lead shares status reports via the Summary
 * status writer's write/edit-time toggle (a later slice).
 *
 * Sharing reuses the existing flexible FeedShareModal (Link / Video / My
 * Content), and shares render through FeedShareCard so they look identical to
 * the community feed. Polymorphic over mini + full projects via entityType.
 * Epic DEV-1295 / DEV-1296. See SPEC_PROJECT_JOURNAL.md.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { type ShareSubmitPayload } from '../CommunityNewsFeed/FeedShareModal';
import { FeedComposer } from '../Feed/FeedComposer';
import { FeedCard } from '../Feed/FeedCard';
import { resolveChronicleType } from '../../Data/Chronicles/chronicleTypes';
import { fetchChronicle } from '../../api/chronicles';
import { fileUrl } from '../../api/fileStorage';
import {
  fetchJournalPosts,
  createJournalPost,
  deleteJournalPost,
  type JournalPost,
  type JournalEntityType,
} from '../../api/journalPosts';
import styles from './JournalTab.module.css';

interface JournalTabProps {
  entityType: JournalEntityType;
  entityGuid: string;
  /** May this viewer share into the Journal? (authorized knowledgeable sharer) */
  canShare: boolean;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** The share comment renders as plain text (like the feed). Strip any stray
 *  tags so legacy HTML-wrapped comments don't show literal markup. */
function plainComment(html: string | null | undefined): string | null {
  if (!html) return null;
  const text = html.replace(/<[^>]+>/g, '').trim();
  return text || null;
}

export default function JournalTab({ entityType, entityGuid, canShare }: JournalTabProps) {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();

  const [posts, setPosts] = useState<JournalPost[]>([]);
  const [loading, setLoading] = useState(true);
  // Chronicle hero images, hydrated from the chronicle API at render time —
  // the same approach the community feed uses (the share payload doesn't carry
  // the full hero image). Keyed by chronicleGuid.
  const [chronicleImages, setChronicleImages] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchJournalPosts(entityType, entityGuid);
      setPosts(rows);

      const guids = [
        ...new Set(
          rows
            .filter((r) => r.source === 'project_chronicle' && r.chronicleGuid)
            .map((r) => r.chronicleGuid as string),
        ),
      ];
      if (guids.length > 0) {
        const map: Record<string, string> = {};
        await Promise.all(
          guids.map(async (g) => {
            try {
              const c = await fetchChronicle(g);
              const url = c.heroOriginalUrl || c.heroImageUrl;
              if (url) map[g] = url;
            } catch {
              // chronicle may have been deleted — ignore
            }
          }),
        );
        setChronicleImages(map);
      } else {
        setChronicleImages({});
      }
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityGuid]);

  useEffect(() => {
    load();
  }, [load]);


  const handleRemove = useCallback(
    async (journalPostGuid: string) => {
      try {
        await deleteJournalPost(entityType, entityGuid, journalPostGuid);
        setPosts((prev) => prev.filter((p) => p.journalPostGuid !== journalPostGuid));
      } catch (err) {
        console.error('[Journal] remove failed', err);
      }
    },
    [entityType, entityGuid],
  );

  // Click-through: My Content opens the chronicle/topic page; links/videos open in a tab.
  const handleCardClick = useCallback(
    (p: JournalPost) => {
      if (p.source === 'project_chronicle' && p.chronicleGuid) {
        navigate(`/chronicles/${p.chronicleGuid}/view`, { state: { from: 'journal' } });
        return;
      }
      if (p.sourceUrl) window.open(p.sourceUrl, '_blank', 'noopener,noreferrer');
    },
    [navigate],
  );

  // Persist the modal's chosen share as a journal post (vs a community-feed share).
  const handleShare = useCallback(
    async (p: ShareSubmitPayload) => {
      if (p.shareType === 'chronicle' && p.chronicle) {
        const td = resolveChronicleType(p.chronicle.categoryKey);
        await createJournalPost(entityType, entityGuid, {
          source: 'project_chronicle',
          chronicleGuid: p.chronicle.chronicleGuid,
          title: p.chronicle.title,
          comment: p.comment ?? null,
          ogTitle: p.chronicle.title,
          ogImageUrl: p.chronicle.heroImageUrl ?? null,
          // Stash the type label as the "source" line for the card.
          ogSiteName: td ? t(td.labelKey) : null,
        });
      } else if (p.url) {
        // link + video both persist as a 'link' reference (a URL).
        await createJournalPost(entityType, entityGuid, {
          source: 'link',
          sourceUrl: p.url,
          title: p.preview?.title ?? null,
          comment: p.comment ?? null,
          ogTitle: p.preview?.title ?? null,
          ogDescription: p.preview?.description ?? null,
          ogImageUrl: p.preview?.imageUrl ?? null,
          ogSiteName: p.preview?.siteName ?? null,
        });
      }
    },
    [entityType, entityGuid, t],
  );

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className={styles.wrap}>
      {canShare && (
        <FeedComposer
          onSubmitShare={handleShare}
          allowText={false}
          contentTabLabel={t('projectDetail.journal.myContent')}
          modalTitle={t('projectDetail.journal.shareModalTitle')}
          onShared={load}
        />
      )}
      <div className={styles.toolbar}>
        <p className={styles.reviewNote}>{t('projectDetail.journal.reviewNote')}</p>
      </div>

      {loading ? null : posts.length === 0 ? (
        <div className={styles.empty}>{t('projectDetail.journal.empty')}</div>
      ) : (
        <div className={styles.list}>
        {posts.map((p) => {
          const isChronicle = p.source === 'project_chronicle';
          const isStatus = p.source === 'status';
          const href = p.sourceUrl || null;
          // Status posts: photo comes from the post's media; narrative renders
          // as HTML. Chronicle posts: hydrated hero. Links: OG image.
          const statusImg = isStatus && p.media && p.media.length > 0
            ? fileUrl(p.media[0].storageKey)
            : null;
          const imageUrl = isStatus
            ? statusImg
            : isChronicle && p.chronicleGuid
              ? chronicleImages[p.chronicleGuid] || p.ogImageUrl
              : p.ogImageUrl;
          const source = isStatus
            ? t('projectDetail.journal.statusTag')
            : p.ogSiteName || (href ? hostOf(href) : t('projectDetail.journal.sharedTag'));
          return (
            <FeedCard
              key={p.journalPostGuid}
              enableComments
              model={{
                id: p.journalPostGuid,
                authorName: source,
                dateText: fmtDate(p.postedAt),
                imageSrc: imageUrl || null,
                theaterImageUrl: imageUrl || null,
                title: p.title || p.ogTitle || (href ? hostOf(href) : null),
                body: isStatus ? null : (plainComment(p.narrativeHtml) || p.ogDescription || null),
                bodyHtml: isStatus ? p.narrativeHtml : null,
                anchorType: 'project_journal_post',
                anchorId: p.journalPostGuid,
                onOpen: () => handleCardClick(p),
                menuActions: canShare
                  ? [{ key: 'remove', danger: true, label: t('projectDetail.journal.removeFromFeed'), onSelect: () => handleRemove(p.journalPostGuid) }]
                  : [],
              }}
            />
          );
        })}
        </div>
      )}
    </div>
  );
}
